import { useRef, useState } from "react";
import { type ClusterItem } from "../../../BearingSelectionAssistant/types";
import { PopperTooltip } from "../../../common/components/Tooltip";

import { Popper } from "@mui/material";
import { Card as LCCard } from "../../../Configurators/Linear/components/Card";
import { LinkButton } from "../../../common/components/Link";
import useMediaQuery from "../../../common/hooks/useMediaQuery";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../utils/screenSizes";
import { useTranslation } from "react-i18next";

interface CharacteristicsIconsProps {
  data: ClusterItem[];
}

export const CharacteristicsIcons = (props: CharacteristicsIconsProps) => {
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  if (!props.data || props.data.length === 0) {
    return null;
  }

  if (isDesktop) {
    return <DesktopCharacteristicsIcons {...props} />;
  }

  return <MobileCharacteristicsIcons {...props} />;
};

const DesktopCharacteristicsIcons = ({ data }: CharacteristicsIconsProps) => {
  const { t } = useTranslation();

  return (
    <div className={"scha__characteristics"}>
      <p className={"scha__characteristics__title headline-small"}>{t("productDetailsPage.characteristics")}</p>
      <div className={"scha__characteristics__item-list"}>
        {data.map((icon, index) => (
          <DesktopIcon key={index} item={icon} />
        ))}
      </div>
    </div>
  );
};

const DesktopIcon = ({ item }: { item: ClusterItem }) => {
  const { val: isHovering, setFalse, setTrue } = useBooleanState(false);
  const { t } = useTranslation();
  return (
    <PopperTooltip
      className={"scha__characteristics__item__tooltip"}
      tooltipContent={<span className={"f-no-wrap"}>{t(item.text)}</span>}
    >
      <img
        className={"scha__characteristics__item__image"}
        src={isHovering ? item.activeImageUrl : item.imageUrl}
        alt={item.text}
        onMouseEnter={setTrue}
        onMouseLeave={setFalse}
      />
    </PopperTooltip>
  );
};

const MobileCharacteristicsIcons = ({ data }: CharacteristicsIconsProps) => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<ClusterItem | null>(null);

  const ref = useRef(null);

  return (
    <>
      <div className={"scha__characteristics"} data-test-id={"characteristics-icons"}>
        <p className={"scha__characteristics__title"}>{t("productDetailsPage.characteristics")}</p>
        <div className={"scha__characteristics__item-list"} ref={ref}>
          {data.map((icon, index) => (
            <LinkButton
              key={index}
              onClick={() => setSelectedItem((prevState) => (prevState === selectedItem ? null : selectedItem))}
            >
              <img
                className={"scha__characteristics__item__image"}
                src={selectedItem === icon ? icon.activeImageUrl : icon.imageUrl}
                alt={icon.text}
              />
            </LinkButton>
          ))}
        </div>
      </div>
      <Popper open={!!selectedItem} anchorEl={ref.current} placement={"top"} className={"scha__popper-tooltip"}>
        <LCCard variant={"square"} disableHover={true} innerClassName={"scha__popper-tooltip__inner"}>
          <span className={"f-no-wrap"}>{t(selectedItem?.text as string)}</span>
        </LCCard>
      </Popper>
    </>
  );
};
